import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CustomBreadcrumb } from '@/shared/components/atoms';
import { CustomCommonScreenHeader } from '@/shared/components/molecules';
import { Home, Network, GitPullRequest, User, Clock, Activity, UserCheck, Flag, CheckCircle2, ArrowLeft, Zap, Eye, Pen, ArrowUpRight, Forward, Settings, MessageSquare, RefreshCcw, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { workflowApi } from '../api/workflowApi';
import { WorkflowStepNew, WorkflowNew } from '../types';
import { useAuthStore } from '@/shared/stores/authStore';
import { useToastStore } from '@/shared/stores/toastStore';
import { useUIStore } from '@/shared/stores/uiStore';

/* ─── Color palette per step ──────────────────────────────── */
const COLORS = [
    { accent: '#f59e0b', bg: '#fffbeb', border: '#fcd34d', shadow: 'rgba(245,158,11,0.18)' },
    { accent: '#8b5cf6', bg: '#f5f3ff', border: '#c4b5fd', shadow: 'rgba(139,92,246,0.18)' },
    { accent: '#10b981', bg: '#ecfdf5', border: '#6ee7b7', shadow: 'rgba(16,185,129,0.18)' },
    { accent: '#f43f5e', bg: '#fff1f2', border: '#fda4af', shadow: 'rgba(244,63,94,0.18)' },
    { accent: '#0ea5e9', bg: '#f0f9ff', border: '#7dd3fc', shadow: 'rgba(14,165,233,0.18)' },
    { accent: '#ec4899', bg: '#fdf2f8', border: '#f9a8d4', shadow: 'rgba(236,72,153,0.18)' },
];

const getStepColors = (index: number, isDark: boolean) => {
    const base = COLORS[index % COLORS.length];
    if (isDark) {
        return {
            accent: base.accent,
            bg: `${base.accent}15`,
            border: `${base.accent}40`,
            shadow: 'rgba(0,0,0,0.2)'
        };
    }
    return base;
};

const PERMS: { key: string; label: string; tip: string }[] = [
    { key: 'can_read', label: 'Read', tip: 'Read Permission' },
    { key: 'can_forward', label: 'Forward', tip: 'Forward Permission' },
    { key: 'can_resolve', label: 'Resolve', tip: 'Resolve Permission' },
    { key: 'can_reply', label: 'Reply', tip: 'Reply Permission' },
    { key: 'can_reopen', label: 'Reopen', tip: 'Reopen Permission' },
];

/* ─── Component ────────────────────────────────────────────── */
export const WorkflowVisualizer = () => {
    const { workflowId } = useParams<{ workflowId: string }>();
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const { showToast } = useToastStore();
    const { theme } = useUIStore();
    const schoolSlug = user?.school?.slug || '';
    const [steps, setSteps] = useState<WorkflowStepNew[]>([]);
    const [workflow, setWorkflow] = useState<WorkflowNew | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [zoom, setZoom] = useState(1);

    useEffect(() => {
        const fetchWorkflowDetails = async () => {
            if (!user?.school?.slug || !workflowId) return;
            setIsLoading(true);
            try {
                const response = await workflowApi.getWorkflowSteps(user.school.slug, workflowId);
                if (response.success && response.data) {
                    setWorkflow(response.data.workflow);
                    setSteps(response.data.steps.sort((a: WorkflowStepNew, b: WorkflowStepNew) => a.order_index - b.order_index));
                } else {
                    showToast(response.error || 'Failed to load workflow details', 'error');
                }
            } catch (error) {
                showToast('An unexpected error occurred', 'error');
            } finally {
                setIsLoading(false);
            }
        };
        fetchWorkflowDetails();
    }, [workflowId, user?.school?.slug, showToast]);

    const initials = (n?: string) => n ? n.split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2) : '?';
    const c = (i: number) => getStepColors(i, theme === 'dark');

    useEffect(() => {
        if (document.getElementById('wf-shimmer-style')) return;
        const style = document.createElement('style');
        style.id = 'wf-shimmer-style';
        style.textContent = `
            @keyframes wfShimmer {
                0% { background-position: -400px 0; }
                100% { background-position: 400px 0; }
            }
            @keyframes wfFadeIn {
                from { opacity: 0; transform: translateY(8px); }
                to { opacity: 1; transform: translateY(0); }
            }
        `;
        document.head.appendChild(style);
    }, []);

    const ShimmerBlock = ({ w, h, r = '8px', mb = '0' }: { w: string; h: string; r?: string; mb?: string }) => (
        <div style={{
            width: w, height: h, borderRadius: r, marginBottom: mb,
            background: 'linear-gradient(90deg, var(--bg-tertiary) 25%, var(--bg-secondary) 50%, var(--bg-tertiary) 75%)',
            backgroundSize: '800px 100%',
            animation: 'wfShimmer 1.5s ease-in-out infinite',
        }} />
    );

    const SkeletonLoader = () => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '2rem 0' }}>
            <ShimmerBlock w="28px" h="28px" r="50%" />
            <ShimmerBlock w="2px" h="18px" r="1px" />
            {[0, 1, 2].map(i => (
                <React.Fragment key={i}>
                    <ShimmerBlock w="160px" h="30px" r="20px" />
                    <ShimmerBlock w="2px" h="14px" r="1px" />
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <ShimmerBlock w="110px" h="100px" r="8px" />
                        <ShimmerBlock w="110px" h="100px" r="8px" />
                        <ShimmerBlock w="110px" h="100px" r="8px" />
                        <ShimmerBlock w="110px" h="100px" r="8px" />
                    </div>
                    {i < 2 && <ShimmerBlock w="2px" h="18px" r="1px" />}
                </React.Fragment>
            ))}
            <ShimmerBlock w="2px" h="18px" r="1px" />
            <ShimmerBlock w="28px" h="28px" r="50%" />
        </div>
    );

    const handleZoom = (type: 'in' | 'out' | 'reset') => {
        if (type === 'reset') setZoom(1);
        else if (type === 'in') setZoom(prev => Math.min(prev + 0.1, 2.0));
        else setZoom(prev => Math.max(prev - 0.1, 0.5));
    };

    return (
        <div style={{ padding: '0.75rem 1rem', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)' }}>
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Workflows', path: `/${schoolSlug}/school-admin/workflows`, icon: <Network size={14} /> },
                    { label: workflow?.departments?.name || 'Department', icon: <Activity size={14} /> }
                ]}
            />
            <CustomCommonScreenHeader
                title={workflow?.name || "Workflow Visualizer"}
                subtitle={workflow?.description || "Visual mapping of the ticketing process."}
                icon={<Activity size={20} />}
                actionLabel="Back"
                actionIcon={<ArrowLeft size={18} />}
                onActionClick={() => navigate(`/${schoolSlug}/school-admin/workflows`)}
                secondaryActionLabel="Configure"
                secondaryActionIcon={<Settings size={16} />}
                onSecondaryActionClick={() => {
                    const deptId = workflow?.departments?.id || workflow?.department_id;
                    navigate(`/${schoolSlug}/school-admin/departments/${deptId}/sub-departments/${workflowId}/levels`);
                }}
            />

            <div style={{
                marginTop: '0.75rem', flex: 1, position: 'relative', overflow: 'hidden',
                background: theme === 'dark'
                    ? 'linear-gradient(160deg, #090d1f 0%, #0c112b 50%, #081a14 100%)'
                    : 'linear-gradient(160deg, #f8fafc 0%, #eef2ff 50%, #f0fdf4 100%)',
                borderRadius: '14px',
                border: theme === 'dark' ? '1px solid var(--border-color)' : '1px solid rgba(226,232,240,0.8)',
                boxShadow: theme === 'dark' ? '0 4px 20px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,0.03)',
            }}>
                {/* ── Fixed Zoom Controls ── */}
                <div style={{
                    position: 'absolute', top: '16px', right: '16px', zIndex: 20,
                    display: 'flex', gap: '4px', background: 'var(--card-bg)', padding: '6px',
                    borderRadius: '12px', boxShadow: theme === 'dark' ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.12)',
                    border: '1px solid var(--border-color)'
                }}>
                    <button onClick={() => handleZoom('in')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', cursor: 'pointer', color: 'var(--text-primary)' }} title="Zoom In"><ZoomIn size={16} /></button>
                    <button onClick={() => handleZoom('out')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', cursor: 'pointer', color: 'var(--text-primary)' }} title="Zoom Out"><ZoomOut size={16} /></button>
                    <button onClick={() => handleZoom('reset')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', cursor: 'pointer', color: 'var(--text-primary)' }} title="Reset Zoom"><RefreshCcw size={16} /></button>
                    <div style={{ borderLeft: '1px solid var(--border-color)', margin: '4px 4px' }} />
                    <span style={{ fontSize: '0.7rem', fontWeight: 800, color: 'var(--text-muted)', padding: '0 8px', alignSelf: 'center', minWidth: '40px', textAlign: 'center' }}>
                        {Math.round(zoom * 100)}%
                    </span>
                </div>

                {/* ── Scrollable Canvas Content ── */}
                <div style={{ height: '100%', overflow: 'auto', padding: '1.25rem 1rem', position: 'relative' }}>
                    {/* dot grid */}
                    <div style={{
                        position: 'absolute', inset: 0, pointerEvents: 'none',
                        backgroundImage: theme === 'dark'
                            ? 'radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)'
                            : 'radial-gradient(circle, rgba(148,163,184,0.10) 1px, transparent 1px)',
                        backgroundSize: '18px 18px',
                    }} />

                    {isLoading ? (
                        <SkeletonLoader />
                    ) : steps.length === 0 ? (
                        <div style={{ textAlign: 'center', paddingTop: '3rem', color: 'var(--text-muted)' }}>
                            <GitPullRequest size={28} style={{ opacity: 0.3, marginBottom: '6px' }} />
                            <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)' }}>No steps found</p>
                            <p style={{ fontSize: '0.7rem' }}>Add steps and levels to visualize your workflow.</p>
                        </div>
                    ) : (
                        <div style={{
                            position: 'relative',
                            zIndex: 1,
                            width: '100%',
                            minHeight: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            transform: `scale(${zoom})`,
                            transformOrigin: 'top center',
                            transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            paddingBottom: '120px',
                            willChange: 'transform',
                            flexShrink: 0,
                        }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{
                                width: '28px', height: '28px', borderRadius: '50%',
                                border: '2px solid #22c55e', background: theme === 'dark' ? 'rgba(34,197,94,0.15)' : '#f0fdf4', color: '#22c55e',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(34,197,94,0.2)',
                            }}>
                                <Zap size={13} />
                            </div>
                            <span style={{ fontSize: '0.5rem', fontWeight: 700, color: 'var(--text-muted)', marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Start</span>
                        </div>

                        {steps.map((step, sIdx) => {
                            const cs = c(sIdx);
                            const levels = [...(step.levels || [])].sort((a: any, b: any) => (a.order_index ?? 0) - (b.order_index ?? 0));
                            return (
                                <React.Fragment key={step.id}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <div style={{ width: '1.5px', height: '18px', background: theme === 'dark' ? 'linear-gradient(to bottom, var(--border-color), var(--border-hover))' : 'linear-gradient(to bottom, #cbd5e1, #94a3b8)' }} />
                                        <div style={{ width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: theme === 'dark' ? '5px solid var(--border-hover)' : '5px solid #94a3b8' }} />
                                    </div>
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: '8px',
                                        background: cs.bg, border: `2px solid ${cs.border}`,
                                        borderRadius: '24px', padding: '6px 16px 6px 8px',
                                        boxShadow: `0 4px 14px ${cs.shadow}`,
                                        maxWidth: '90%',
                                        zIndex: 2,
                                    }}>
                                        <div style={{
                                            width: '28px', height: '28px', borderRadius: '50%',
                                            background: cs.accent, display: 'flex', alignItems: 'center',
                                            justifyContent: 'center', color: 'white', flexShrink: 0,
                                        }}>
                                            <Flag size={14} />
                                        </div>
                                        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {step.name}
                                        </span>
                                        <span style={{ fontSize: '0.55rem', fontWeight: 700, color: cs.accent, background: 'var(--bg-primary)', borderRadius: '8px', padding: '1px 6px', border: `1px solid ${cs.border}`, flexShrink: 0 }}>
                                            S{sIdx + 1}
                                        </span>
                                        {step.sla_hours != null && (
                                            <div style={{
                                                display: 'flex', alignItems: 'center', gap: '3px',
                                                fontSize: '0.65rem', fontWeight: 700, color: 'white',
                                                background: '#ef4444', padding: '2px 10px', borderRadius: '12px',
                                                marginLeft: '8px', boxShadow: '0 2px 6px rgba(239,68,68,0.3)',
                                                flexShrink: 0
                                            }}>
                                                <Clock size={11} />
                                                <span>{step.sla_hours}h limit</span>
                                            </div>
                                        )}
                                    </div>
                                    {levels.length > 0 && (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                                            <div style={{ width: '1.5px', height: '14px', background: cs.border }} />
                                            <div style={{
                                                position: 'relative', display: 'flex', justifyContent: 'center',
                                                flexWrap: 'wrap', gap: '0', width: '100%', maxWidth: '800px',
                                            }}>
                                                {levels.length > 1 && (
                                                    <div style={{
                                                        position: 'absolute', top: '0px',
                                                        left: `calc(50% - ${Math.min(levels.length, 4) * 55}px + 55px)`,
                                                        right: `calc(50% - ${Math.min(levels.length, 4) * 55}px + 55px)`,
                                                        height: '1.5px', background: cs.border,
                                                    }} />
                                                )}
                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: `repeat(${Math.min(levels.length, 4)}, 110px)`,
                                                    gap: '8px 12px', justifyContent: 'center',
                                                }}>
                                                    {levels.map((lv: any, li: number) => (
                                                        <div key={lv.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                            <div style={{ width: '1.5px', height: '10px', background: cs.border }} />
                                                            <div style={{
                                                                background: 'var(--card-bg)', border: `1px solid ${cs.border}`,
                                                                borderRadius: '10px', padding: '8px 6px 8px',
                                                                width: '110px', display: 'flex', flexDirection: 'column',
                                                                alignItems: 'center', gap: '4px', position: 'relative',
                                                                boxShadow: theme === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : `0 2px 8px ${cs.shadow}`,
                                                                transition: 'transform 0.15s, box-shadow 0.15s',
                                                                cursor: 'default',
                                                            }}
                                                                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = theme === 'dark' ? '0 6px 18px rgba(0,0,0,0.5)' : `0 6px 18px ${cs.shadow}`; }}
                                                                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = theme === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : `0 2px 8px ${cs.shadow}`; }}
                                                            >
                                                                <div style={{
                                                                    position: 'absolute', top: '4px', left: '5px',
                                                                    fontSize: '0.45rem', fontWeight: 700, color: cs.accent,
                                                                    background: cs.bg, padding: '0 4px', borderRadius: '4px',
                                                                    border: `1px solid ${cs.border}`,
                                                                }}>L{li + 1}</div>
                                                                <div style={{
                                                                    width: '28px', height: '28px', borderRadius: '50%',
                                                                    background: cs.accent, color: 'white', display: 'flex',
                                                                    alignItems: 'center', justifyContent: 'center',
                                                                    fontSize: '0.65rem', fontWeight: 700, marginTop: '4px',
                                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                                }}>
                                                                    {lv.assigned_employee?.name ? initials(lv.assigned_employee.name) : <User size={14} />}
                                                                </div>
                                                                <div style={{
                                                                    fontSize: '0.6rem', fontWeight: 700, color: 'var(--text-primary)',
                                                                    textAlign: 'center', width: '100%',
                                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                                }}>{lv.name}</div>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0px', width: '100%', alignItems: 'center' }}>
                                                                    <div style={{
                                                                        fontSize: '0.52rem', fontWeight: 600, color: 'var(--text-secondary)', textAlign: 'center',
                                                                        width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                                    }}>{lv.assigned_employee?.name || 'Unassigned'}</div>
                                                                    {(lv.assigned_employee?.designation || (lv.assigned_employee?.departments && lv.assigned_employee.departments.length > 0)) && (
                                                                        <div style={{
                                                                            fontSize: '0.48rem', fontWeight: 600, color: 'var(--text-muted)', textAlign: 'center',
                                                                            width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                                        }}>
                                                                            {lv.assigned_employee.designation || ''}
                                                                            {lv.assigned_employee.departments && lv.assigned_employee.departments.length > 0 && (
                                                                                <>
                                                                                    {lv.assigned_employee.designation ? ' (' : ''}
                                                                                    {lv.assigned_employee.departments.join(', ')}
                                                                                    {lv.assigned_employee.designation ? ')' : ''}
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                    {lv.assigned_employee?.email && (
                                                                        <div style={{
                                                                            fontSize: '0.48rem', color: 'var(--text-muted)', textAlign: 'center',
                                                                            width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                                        }}>{lv.assigned_employee.email}</div>
                                                                    )}
                                                                </div>
                                                                <div style={{ display: 'flex', gap: '2px', marginTop: '2px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                                                    {PERMS.filter(p => lv[p.key]).map(p => (
                                                                        <span key={p.key} title={p.tip} style={{
                                                                            fontSize: '0.4rem', fontWeight: 800, color: cs.accent,
                                                                            background: `${cs.accent}12`,
                                                                            border: `1px solid ${cs.accent}20`,
                                                                            padding: '0px 4px', borderRadius: '10px',
                                                                            textTransform: 'uppercase', lineHeight: '1.2'
                                                                        }}>{p.label}</span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {levels.length === 0 && (
                                        <div style={{ fontSize: '0.5rem', color: 'var(--text-muted)', marginTop: '2px' }}>No levels mapped</div>
                                    )}
                                </React.Fragment>
                            );
                        })}

                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{ width: '1.5px', height: '18px', background: theme === 'dark' ? 'linear-gradient(to bottom, var(--border-color), var(--border-hover))' : 'linear-gradient(to bottom, #cbd5e1, #94a3b8)' }} />
                            <div style={{ width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderTop: theme === 'dark' ? '5px solid var(--border-hover)' : '5px solid #94a3b8' }} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{
                                width: '28px', height: '28px', borderRadius: '50%',
                                border: '2px solid #ef4444', background: theme === 'dark' ? 'rgba(239,68,68,0.15)' : '#fef2f2', color: '#ef4444',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(239,68,68,0.2)',
                            }}>
                                <CheckCircle2 size={13} />
                            </div>
                            <span style={{ fontSize: '0.5rem', fontWeight: 700, color: 'var(--text-muted)', marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>End</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
        </div>
    );
};
